import React from 'react';
import { CouncilConfig, getCouncilConfig } from '../../lib/councils';

function formatDate(value: string, previousDay = false) {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return null;
  if (previousDay) date.setUTCDate(date.getUTCDate() - 1);
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
  }).format(date);
}

export function sourceCoverageCopy(council: CouncilConfig) {
  const coverage = council.sourceCoverage;
  if (!coverage) return 'Coverage is still being checked. More records may be added.';
  if (coverage.status === 'unverified') return coverage.notice || 'Current source coverage and updates are unverified.';
  const { sourceCount, capturedCount, missingCount } = coverage;
  if (sourceCount === null || missingCount === null) return 'Coverage is still being checked. More records may be added.';
  const counts = [sourceCount, capturedCount, missingCount];
  const start = formatDate(coverage.windowStart);
  const end = formatDate(coverage.windowEndExclusive, true);
  const checked = formatDate(coverage.verifiedAt);
  if (!counts.every((value) => Number.isSafeInteger(value) && value >= 0)
      || capturedCount + missingCount !== sourceCount || !start || !end || !checked
      || new Date(coverage.windowStart) >= new Date(coverage.windowEndExclusive)
      || !['received', 'validated'].includes(coverage.dateBasis)) {
    return 'Coverage is still being checked. More records may be added.';
  }
  const period = `${start} – ${end}`;
  const numbers = `${capturedCount.toLocaleString('en-GB')} of ${sourceCount.toLocaleString('en-GB')}`;
  return `${numbers} official records ${coverage.dateBasis} ${period} were available when checked on ${checked}.`
    + (missingCount ? ` ${missingCount.toLocaleString('en-GB')} records from that period are still being added or checked.` : '');
}

export default function CouncilCoverageNotice({
  councilSlug, council: suppliedCouncil, compact = false, mapView = false,
}: { councilSlug?: string | null; council?: CouncilConfig | null; compact?: boolean; mapView?: boolean }) {
  const council = suppliedCouncil || (councilSlug ? getCouncilConfig(councilSlug) : null);
  if (!council || (council.releaseStage !== 'official_records' && !council.sourceCoverage)) return null;
  const portal = council.sourcePortalUrl || council.publicSearchUrl;
  const sourceUrl = portal?.startsWith('https://') ? portal : null;
  const copy = sourceCoverageCopy(council);
  const coverageComplete = council.sourceCoverage?.status === 'complete'
    && council.sourceCoverage.missingCount === 0 && !copy.startsWith('Coverage is');
  return (
    <details className={`min-w-0 border border-[var(--c-border)] bg-[var(--c-surface)] text-[var(--c-text-secondary)] ${compact ? 'px-3 py-2 text-xs' : 'my-4 px-4 py-3 text-sm'}`}>
      <summary className="cursor-pointer font-semibold leading-6 text-[var(--c-text-primary)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--c-accent)]">
        {council.sourceCoverage?.status === 'unverified' ? 'Historical records · current coverage unverified' : `Official records · ${coverageComplete ? 'analysis in progress' : 'coverage in progress'}`}
      </summary>
      <div className="mt-2 max-w-3xl space-y-2 leading-6">
        <p>{copy}</p>
        {mapView ? <p>The map shows records with available locations. <a className="underline underline-offset-4" href={`/planning-updates/${council.shortCode}`}>Browse all available records in Planning Updates.</a></p> : null}
        {council.region === 'london'
          ? <p>These are source records for {council.displayName}. Each application links to its official source. Complete council-register coverage is still being checked.</p>
          : <p>Proposals and decisions come from {council.officialName}. Additional summaries and analysis are being added where verified.</p>}
        {sourceUrl ? (
          <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className="inline-block underline underline-offset-4 hover:text-[var(--c-accent)]">
            Check the council’s planning register
          </a>
        ) : null}
      </div>
    </details>
  );
}
